import * as AidiaCrypto from "./crypto.js";
/**
 * AIDIA biometrics — WebAuthn (Windows Hello / Touch ID / platform fingerprint).
 *
 * Two capabilities:
 *  - Unlock: uses the WebAuthn PRF extension to derive a stable secret from the
 *    authenticator, which wraps (encrypts) the master password. The biometric
 *    releases that secret so the vault can be unlocked without typing the password.
 *    PRF isn't universal, so this is offered only where supported.
 *  - Reveal re-auth: a plain biometric presence check (no PRF needed) to gate
 *    revealing a stored password while the vault is already unlocked.
 *
 * The stored record (aidia.bio) holds the credential id, a fixed PRF salt, and the
 * PRF-wrapped master password — all safe at rest (the wrapped password is ciphertext
 * only the biometric-derived key can open).
 */
const _mod = (() => {
  const KEY = "aidia.bio";
  const RP_ID = location.hostname || "localhost";

  const enc = new TextEncoder();
  const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
  const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
  const rand = (n) => crypto.getRandomValues(new Uint8Array(n));

  const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
  const save = (rec) => localStorage.setItem(KEY, JSON.stringify(rec));
  const disable = () => localStorage.removeItem(KEY);

  const isSupported = () => !!(window.PublicKeyCredential && navigator.credentials);
  const isEnrolled = () => !!load();
  const mode = () => (load() || {}).mode || null;
  const canUnlock = () => mode() === "prf";
  const canReveal = () => isEnrolled();

  async function platformAvailable() {
    if (!isSupported()) return false;
    try { return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); }
    catch { return false; }
  }

  // Run a get() assertion and return its PRF "first" result (ArrayBuffer) or null.
  async function getPrf(credId, prfSalt) {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: rand(32),
        rpId: RP_ID,
        allowCredentials: [{ type: "public-key", id: credId }],
        userVerification: "required",
        timeout: 60000,
        extensions: { prf: { eval: { first: prfSalt } } },
      },
    });
    const res = assertion.getClientExtensionResults();
    return res && res.prf && res.prf.results ? res.prf.results.first : null;
  }

  /**
   * Enrol this device. Requires the master password so it can be wrapped under the
   * biometric-derived key. Returns { mode } — "prf" (full unlock) or "presence"
   * (reveal-only, when PRF isn't available).
   */
  async function enroll(masterPassword) {
    if (!isSupported()) throw new Error("Biometrics aren't supported in this browser.");
    const cred = await navigator.credentials.create({
      publicKey: {
        challenge: rand(32),
        rp: { id: RP_ID, name: "AIDIA" },
        user: { id: rand(16), name: "aidia-user", displayName: "AIDIA User" },
        pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
          residentKey: "preferred",
        },
        timeout: 60000,
        extensions: { prf: {} },
      },
    });
    if (!cred) throw new Error("Enrolment was cancelled.");
    const credId = cred.rawId;
    const ext = cred.getClientExtensionResults ? cred.getClientExtensionResults() : {};
    const prfEnabled = ext && ext.prf && ext.prf.enabled;

    if (prfEnabled) {
      try {
        const prfSalt = rand(32);
        const prfOut = await getPrf(credId, prfSalt);
        if (prfOut) {
          const key = await AidiaCrypto.importAesKey(new Uint8Array(prfOut));
          const wrapped = await AidiaCrypto.encryptWith(key, masterPassword);
          save({ credId: toB64(credId), mode: "prf", prfSalt: toB64(prfSalt), wrapped });
          return { mode: "prf" };
        }
      } catch (e) {
        console.warn("PRF wrap failed, falling back to presence mode", e);
      }
    }
    // Presence-only: can gate reveals, but cannot unlock (no key material available).
    save({ credId: toB64(credId), mode: "presence" });
    return { mode: "presence" };
  }

  /** Biometric unlock → returns the master password, or null if unavailable/failed. */
  async function unlock() {
    const rec = load();
    if (!rec || rec.mode !== "prf") return null;
    try {
      const prfOut = await getPrf(fromB64(rec.credId), fromB64(rec.prfSalt));
      if (!prfOut) return null;
      const key = await AidiaCrypto.importAesKey(new Uint8Array(prfOut));
      return await AidiaCrypto.decryptWith(key, rec.wrapped); // the master password
    } catch (e) {
      console.warn("Biometric unlock failed", e);
      return null;
    }
  }

  /** Presence check for re-auth (vault already unlocked). Returns true on success. */
  async function verifyPresence() {
    const rec = load();
    if (!rec) return false;
    try {
      await navigator.credentials.get({
        publicKey: {
          challenge: rand(32),
          rpId: RP_ID,
          allowCredentials: [{ type: "public-key", id: fromB64(rec.credId) }],
          userVerification: "required",
          timeout: 60000,
        },
      });
      return true;
    } catch {
      return false;
    }
  }

  return {
    isSupported, isEnrolled, mode, canUnlock, canReveal, platformAvailable,
    enroll, unlock, verifyPresence, disable,
  };
})();

// --- ES module surface -------------------------------------------------
export const { isSupported, isEnrolled, mode, canUnlock, canReveal, platformAvailable, enroll, unlock, verifyPresence, disable } = _mod;
export default _mod;
